import { isAxiosError } from 'axios';

import { i18n } from '@/locales';

import { ToastType } from '@/stores/toastSnackbar.store';

const t = i18n.global.t;
interface Errors {
  [key: string]: string[];
}

export const getContentErrors = (errors: Errors) => {
  let content = '';
  for (const err in errors) {
    errors[err].forEach((message: string) => {
      if (content !== '') {
        content += ' - ';
      }
      content += message;
    });
  }
  return content;
};

interface GetErrorToasterConfigParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  toasterId: string;
  defaultMessage: string;
}

//@deprecated
export const getErrorToasterConfig = ({
  error,
  toasterId,
  defaultMessage,
}: GetErrorToasterConfigParams) => {
  let errorMessage;

  if (isAxiosError(error) && error.response?.data) {
    const { errors, detail } = error.response.data;
    errorMessage = errors ? getContentErrors(error.response?.data?.errors) : detail;
  }

  errorMessage = errorMessage?.length ? errorMessage : defaultMessage;

  return {
    id: toasterId,
    title: t('common.apiError'),
    content: errorMessage,
    timer: 0,
    toastType: ToastType.Error,
  };
};

/**
 * Error thrown by the API layer when a request fails. Pass the raw caught
 * error — `ApiError` reads both backend formats from the Axios response so
 * the rest of the app never touches it:
 *  - `code`: the raw `detail`, a candidate functional code to feed to
 *    `matchErrorCode`.
 *  - `displayMessage`: a ready-to-show message — the concatenated `errors`
 *    map when present, otherwise the free-text `detail`.
 * Consumers resolve the final message with `resolveApiErrorMessage`.
 */
export class ApiError extends Error {
  readonly code?: string;
  readonly displayMessage?: string;
  readonly status?: number;

  constructor(error?: unknown) {
    const response = isAxiosError(error) ? error.response : undefined;
    const data = response?.data;
    const detail = typeof data?.detail === 'string' ? data.detail : undefined;
    const displayMessage = data?.errors ? getContentErrors(data.errors) : detail;
    super(displayMessage || 'API_ERROR');
    this.name = 'ApiError';
    this.code = detail;
    this.displayMessage = displayMessage;
    this.status = response?.status;
  }
}

/**
 * Narrows an unknown error to a known functional code for a given endpoint.
 * Returns the typed code when `error` is an `ApiError` carrying one of
 * `knownCodes`, otherwise `undefined`. Reusable across endpoints — pass the
 * endpoint's error enum as `knownCodes`.
 */
export const matchErrorCode = <T extends string>(
  error: unknown,
  knownCodes: Record<string, T>
): T | undefined =>
  error instanceof ApiError && Object.values<string>(knownCodes).includes(error.code ?? '')
    ? (error.code as T)
    : undefined;

interface ResolveApiErrorMessageOptions<T extends string> {
  /** The endpoint's functional error codes (e.g. `EDirectDebitErrorType`). */
  knownCodes: Record<string, T>;
  /** Maps a known code to its localized message (usually an i18n lookup). */
  resolveCode: (code: T) => string;
  /** Shown when the backend returns neither a known code nor a direct message. */
  defaultMessage: string;
}

/**
 * Resolves the message to show for an error, adapting to the backend format:
 *  1. a known functional code in `detail` → `resolveCode(code)`
 *  2. a direct backend message (`errors` map or free-text `detail`) → as-is
 *  3. otherwise → `defaultMessage`
 * Reusable across endpoints; each caller supplies its own codes and mapping.
 */
export const resolveApiErrorMessage = <T extends string>(
  error: unknown,
  { knownCodes, resolveCode, defaultMessage }: ResolveApiErrorMessageOptions<T>
): string => {
  const code = matchErrorCode(error, knownCodes);
  if (code) return resolveCode(code);

  if (error instanceof ApiError && error.displayMessage) return error.displayMessage;

  return defaultMessage;
};
